import type { LineChartProps } from '@shopify/polaris-viz';
import type { IAnalyticMetric } from '~/types/metric';
import type { ICampaignMetricKey } from '../../types';
import { AnalyticInterval } from '../../types';

import { formatMetricByFormatter, getFormattedByInterval } from '~/modules/analytics';
import { AnalyticLineChartTooltip } from './AnalyticLineChartTooltip';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { GSkeletonDisplayText } from '~/presentation/common';
import { useCampaignAnalyticMetricFunc } from '../../hooks';
import { getCampaignAnalyticMapping } from '../../helpers';
import { IVariantType } from '~/modules/campaign/types';
import { GChartProvider } from '~/presentation/charts';
import { BlockStack, Box } from '@shopify/polaris';
import { LineChart } from '@shopify/polaris-viz';

import { useMemo } from 'react';

const LABEL_BY_VARIANT_KEY = {
  [IVariantType.VARIANT_A]: 'Control',
  [IVariantType.VARIANT_B]: 'Variant',
};
export interface IAnalyticLineChartData {
  metricInterval: {
    data: IAnalyticMetric[];
    variantKey: IVariantType;
  }[];
  interval?: AnalyticInterval;
}

export interface AnalyticLineChartProps {
  metricKey: ICampaignMetricKey;
  isLoading?: boolean;
  data: IAnalyticLineChartData;
}

export const AnalyticLineChart: React.FC<AnalyticLineChartProps> = ({
  metricKey,
  isLoading,
  data,
}) => {
  const funcGetMetric = useCampaignAnalyticMetricFunc();

  const { getTextPrice } = useConvertMoneyByCurrency();
  const { metricInterval, interval = AnalyticInterval.DAY } = data;

  const metricFuncKey = useMemo(() => {
    return getCampaignAnalyticMapping(metricKey).metricFuncKey;
  }, [metricKey]);

  const lineChartData = useMemo(() => {
    return [IVariantType.VARIANT_A, IVariantType.VARIANT_B].map((variantKey) => {
      const data = metricInterval.find((item) => item.variantKey === variantKey)?.data;
      return {
        name: variantKey,
        data:
          data?.map((item) => {
            const value = funcGetMetric?.[metricFuncKey]?.({ metric: item }).value;
            return {
              key: item.date || '',
              value: Number(value) || 0,
            };
          }) || [],
      };
    });
  }, [funcGetMetric, metricInterval, metricFuncKey]);

  const xAxisOptions: LineChartProps['xAxisOptions'] = useMemo(() => {
    return {
      labelFormatter: (value) => {
        return getFormattedByInterval(value, interval);
      },
    };
  }, [interval]);

  const yAxisOptions: LineChartProps['yAxisOptions'] = useMemo(() => {
    return {
      labelFormatter: (value) => {
        const formatter = funcGetMetric?.[metricFuncKey]?.({ metric: undefined }).formatter;
        const valueFormatted = formatMetricByFormatter({
          value: Number(value) || 0,
          formatter,
          getTextPrice,
        });
        return valueFormatted;
      },
    };
  }, [metricFuncKey, funcGetMetric, getTextPrice]);

  const tooltipOptions: LineChartProps['tooltipOptions'] = useMemo(() => {
    return {
      titleFormatter: (value) =>
        getFormattedByInterval(value, interval, {
          isExpandDetail: true,
        }),
      valueFormatter: (value) => {
        const formatter = funcGetMetric?.[metricFuncKey]?.({ metric: undefined }).formatter;
        const valueFormatted = formatMetricByFormatter({
          value: Number(value),
          formatter,
          getTextPrice,
        });
        return valueFormatted;
      },
      renderTooltipContent: (data) => {
        return <AnalyticLineChartTooltip data={data} metricKey={metricKey} />;
      },
    };
  }, [metricFuncKey, funcGetMetric, getTextPrice, interval, metricKey]);

  if (isLoading) {
    return <GSkeletonDisplayText height="228px" />;
  }

  return (
    <Box paddingInlineEnd={'300'} paddingInlineStart={'050'}>
      <BlockStack gap="400">
        <GChartProvider>
          <LineChart
            data={lineChartData}
            xAxisOptions={xAxisOptions}
            yAxisOptions={yAxisOptions}
            tooltipOptions={tooltipOptions}
            seriesNameFormatter={(x) => LABEL_BY_VARIANT_KEY[x as IVariantType]}
            theme="Light"
            showLegend
          />
        </GChartProvider>
      </BlockStack>
    </Box>
  );
};
